import { useEffect, useRef, useState } from 'react';

import './benchmark-activities.css';

import BenchmarkTradeDisplay from './BenchmarkTradeDisplay';

import { EnumSelectDropdown, EnumSelectDropdownHandle } from '../Filters/EnumSelectDropdown';
import WordSearchInput, { WordSearch, WordSearchInputHandle } from '../Filters/WordSearchInput';

import Editable from '../Icons/Editable';
import ResetFilter from '../Icons/ResetFilter';
import Uneditable from '../Icons/Uneditable';

import { ActiveInactiveStatus } from '../../enums/ActiveInactiveStatus';

import { Activity } from '../../types/Quality/Activity';

/**
 * this is at the file global level so that all the support components have
 *   in this file have access as necessary
 */
const { Active, Inactive } = ActiveInactiveStatus;

type BenchmarkActivitiesTableProps = {
    activityList: Activity[],
    onActivityEdit: (id: number) => void,
    canEdit: boolean,
};

const BenchmarkActivitiesTable = ( { activityList, onActivityEdit, canEdit }: BenchmarkActivitiesTableProps ) => {
    const [nameFilter, setNameFilter] = useState<string>('');
    const [tradeFilter, setTradeFilter] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<ActiveInactiveStatus | undefined>(Active);

    const [filteredList, setFilteredList] = useState<Activity[]>(activityList);

    const [acivityTableKey, setActvityTableKey] = useState<string>('activityTableKey' + Date.now());

    const nameRef = useRef<WordSearchInputHandle>(null);
    const tradeRef = useRef<WordSearchInputHandle>(null);
    const statusRef = useRef<EnumSelectDropdownHandle>(null);

    /**
     * This is where we will filter down the available Activities based off of
     *   the user input filters
     */
    useEffect(() => {
        const newList: Activity[] = activityList.filter((a) => {
            const normalizedNameFilter = nameFilter?.toLowerCase().trim() ?? '';
            const normalizedTradeFilter = tradeFilter?.toLowerCase().trim() ?? '';

            const activityMatch = normalizedNameFilter ? a.name.toLowerCase().includes(normalizedNameFilter) : true;
            const tradeMatch = normalizedTradeFilter ? (a.trades.length > 0  && a.trades.some((t) => t.name.toLowerCase().includes(normalizedTradeFilter))) : true;
            const statusMatch = (statusFilter === undefined || (statusFilter === Active && a.isActive) || (statusFilter === Inactive && !a.isActive));
            return activityMatch && tradeMatch && statusMatch;
        });

        setFilteredList(newList);
        setActvityTableKey('activityTableKey' + Date.now());

    }, [activityList, nameFilter, tradeFilter, statusFilter]);

    const resetFilters = () => {
        nameRef.current?.clear();
        tradeRef.current?.clear();
        statusRef.current?.clear();
        setNameFilter('');
        setTradeFilter('');
        setStatusFilter(undefined);
    };

    /**
     * This makes the building of the activity rows easy to render
     *   and allow for us to switch to the "no list" render when needed
     * @returns Table rows for the benchmark activities
     */
    const renderList= () => (
        filteredList.map((activity) => (
        <tr
            key={`${activity.name}`}
        >
            <td className='px-2'>
                {activity.name}
            </td>
            <td className='px-2'>
                <BenchmarkTradeDisplay
                    trades={activity.trades}
                />
            </td>
            <td className='px-2'>
                {activity.isActive ? 'Active' : 'Inactive'}
            </td>
            <td className='px-2'>
                { canEdit &&
                    <button
                        className={`btn benchmark-activity-edit-button ${activity.isActive ? '' : 'border-0'}`}
                        title={`Edit ${activity.name}: ${activity.id}`}
                        onClick={() => onActivityEdit(activity.id)}
                        disabled={!activity.isActive}
                    >
                        { !activity.isActive ? <Uneditable /> : <Editable /> }
                    </button>
                }
            </td>
        </tr>
    )));

    /**
     * This puts the building of what is displayed when no rows are available
     *   and allow for us to switch to the "list" render when needed
     * @returns No Table rows for the benchmark activities
     */
    const renderNoList = () => {
        return (
            <tr>
                <td className='px-2'>
                    <div className='text-muted'>
                        None
                    </div>
                </td>
                <td className='px-2'>
                    <div className='text-muted'>
                        None
                    </div>
                </td>
                <td className='px-2'>
                    <div className='text-muted'>
                        None
                    </div>
                </td>
                <td className='px-2'>
                </td>
            </tr>
        )
    };

    return (
        <div className='border rounded-2 shadow-sm ba'>
            <div className='benchmark-activities-controls mb-1'>
                <div>
                    <WordSearchInput
                        ref={nameRef}
                        label='Activity Name'
                        wrapperClassName='mb-0'
                        placeholder='Filter...'
                        onChange={(search: WordSearch) => setNameFilter(search.value?.toLowerCase().trim() ?? '')}
                    />
                </div>
                <div>
                    <WordSearchInput
                        ref={tradeRef}
                        label='Trades'
                        wrapperClassName='mb-0'
                        placeholder='Filter...'
                        onChange={(search: WordSearch) => setTradeFilter(search.value?.toLowerCase().trim() ?? '')}
                    />
                </div>
                <div>
                    <EnumSelectDropdown
                        ref={statusRef}
                        wrapperClassName='mb-0'
                        label='Status'
                        enumObj={ActiveInactiveStatus}
                        defaultValue={Active}
                        onChange={(e) => setStatusFilter(e)}
                    />
                </div>
                <div>
                    <label className="form-label">&nbsp;</label>
                    <button
                        title='Reset all filters'
                        className='ba-reset-btn mb-1'
                        onClick={resetFilters}
                    >
                        <ResetFilter />
                    </button>
                </div>
            </div>
            {/* scroll the wrapper, not <tbody> */}
            <div className='benchmark-activities-table-container'>
                <table className='benchmark-activities-table my-2'>
                    <colgroup>
                        <col className='ba-col-1' />
                        <col className='ba-col-2' />
                        <col className='ba-col-3' />
                        <col className='ba-col-4' />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className='benchmark-activity-header'>{/* Activity Name */}</th>
                            <th className='benchmark-activity-header'>{/* Trades */}</th>
                            <th className='benchmark-activity-header'>{/* Status */}</th>
                            <th className='benchmark-activity-header'>{/* Edit Icon / Reset */}</th>
                        </tr>
                    </thead>
                    <tbody key={acivityTableKey}>
                        {filteredList.length > 0 ? renderList() : renderNoList()}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default BenchmarkActivitiesTable;